import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { io } from 'socket.io-client'
import { Link } from 'react-router-dom'
import { getLeaderboard, getTopStakers } from '../services/leaderboard'
import { getProfile } from '../services/profiles'
import { getAccessToken } from '../store/authStore'
import { useAuth } from '../context/AuthContext'
import { CIS_LEVELS, CIS_MAX, formatMultiplier } from '../utils/cisLevels'

const _rawApiBase = import.meta.env.VITE_API_BASE_URL ?? ''
const SOCKET_ORIGIN = _rawApiBase.replace(/\/?api\/?$/, '').replace(/\/+$/, '')

// ─── CIS Constants ────────────────────────────────────────────
// CIS_MAX now comes from utils/cisLevels (the real backend ceiling,
// CIS_TOTAL_MAX = 10,000,000) — see that file for why. NOTE: this makes
// the "% of max" displays below run very low for realistic scores, since
// the real system spans 4 orders of magnitude (literal per-action points,
// not a normalized 0–100k split). Flagging as a UX follow-up, not fixing
// the display approach itself here — out of scope for this pass.
const SOCIAL_MAX      = 70_000
const STAKING_MAX     = 20_000
const CONSISTENCY_MAX = 10_000

// ─── Tier config ──────────────────────────────────────────────
// Sourced from the shared CIS_LEVELS config (utils/cisLevels.js) so level
// requirements and reward multipliers stay in sync with the level info
// page, profile, and offerwall — just re-ordered Diamond→Bronze for the
// legend/podium display here.
const TIERS = [...CIS_LEVELS].reverse()

// ─── Tab config ───────────────────────────────────────────────
const TABS = [
  { key: 'influence',   label: 'CIS',         icon: '/icons/creators.png',    desc: 'Full Chicago Influence Score — social + staking + consistency' },
  { key: 'creators',    label: 'Creators',    icon: '/icons/creators.png',    desc: 'Ranked by social activity: posts, likes and comments' },
  { key: 'stakers',     label: 'Stakers',     icon: '/icons/stakers.png',     desc: 'Ranked by CLT staked and remaining lock duration' },
  { key: 'consistency', label: 'Consistency', icon: '/icons/consistency.png', desc: 'Ranked by daily activity streak and account tenure' },
]

// TIERS is CIS_LEVELS reversed (Diamond→Bronze), so matching top-down on
// min alone finds the correct highest tier — mirrors backend's
// resolveCisBadge (cis-level.constant.ts) and works for Diamond's
// open-ended max (null) with no special-casing needed.
function getTier(score) {
  return TIERS.find(t => score >= t.min) ?? TIERS[TIERS.length - 1]
}

// ─── IllustrationIcon ─────────────────────────────────────────
function IllustrationIcon({ src, alt, size, className = '' }) {
  const [loaded, setLoaded] = useState(false)
  const [error,  setError]  = useState(false)
  return (
    <span
      className={`relative inline-flex items-center justify-center shrink-0 ${className}`}
      style={{ width: size, height: size }}
    >
      {!loaded && (
        <span
          className="absolute inset-0 rounded bg-on-background/10 animate-pulse"
          style={{ width: size, height: size }}
        />
      )}
      {!error && (
        <img
          src={src}
          alt={alt}
          width={size}
          height={size}
          onLoad={() => setLoaded(true)}
          onError={() => { setLoaded(true); setError(true) }}
          className="object-contain"
          style={{
            width: size,
            height: size,
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.4s ease',
          }}
        />
      )}
    </span>
  )
}

// ─── TabIcon ──────────────────────────────────────────────────
function TabIcon({ src, alt, size = 36, active = false }) {
  const [loaded, setLoaded] = useState(false)
  const [error,  setError]  = useState(false)
  const filter = active ? 'brightness(0) invert(1)' : 'none'
  return (
    <span
      className="relative inline-flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
    >
      {!loaded && (
        <span
          className="absolute inset-0 rounded bg-on-background/10 animate-pulse"
          style={{ width: size, height: size }}
        />
      )}
      {!error && (
        <img
          src={src}
          alt={alt}
          width={size}
          height={size}
          onLoad={() => setLoaded(true)}
          onError={() => { setLoaded(true); setError(true) }}
          className="object-contain"
          style={{
            width: size,
            height: size,
            opacity: loaded ? 1 : 0,
            filter,
            transition: 'opacity 0.35s ease, filter 0.2s ease',
          }}
        />
      )}
    </span>
  )
}

// ─── TierIcon ─────────────────────────────────────────────────
function TierIcon({ src, alt, size = 16 }) {
  const [loaded, setLoaded] = useState(false)
  const [error,  setError]  = useState(false)
  return (
    <span className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
      {!loaded && (
        <span className="absolute inset-0 bg-on-background/10 animate-pulse rounded-sm" />
      )}
      {!error && (
        <img
          src={src}
          alt={alt}
          width={size}
          height={size}
          onLoad={() => setLoaded(true)}
          onError={() => { setLoaded(true); setError(true) }}
          className="object-contain"
          style={{ width: size, height: size, opacity: loaded ? 1 : 0, transition: 'opacity 0.3s ease' }}
        />
      )}
    </span>
  )
}

// ─── Avatar ───────────────────────────────────────────────────
function Avatar({ src, alt, size = 'w-10 h-10' }) {
  const [loaded, setLoaded] = useState(false)
  const [imgSrc, setImgSrc] = useState(src)

  // Same fix as PodiumAvatar: keep imgSrc in sync if `src` changes after
  // mount, instead of only seeding it once. Table rows are already keyed
  // by entry.id so this isn't currently reachable here, but it matches
  // PodiumAvatar's behavior and avoids the same trap if that ever changes.
  useEffect(() => {
    setImgSrc(src)
    setLoaded(false)
  }, [src])

  return (
    <div className={`relative shrink-0 ${size} border border-on-background/20 overflow-hidden bg-surface-container`}>
      {!loaded && <div className="absolute inset-0 bg-on-background/10 animate-pulse" />}
      <img
        src={imgSrc}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onError={() => { setImgSrc('/favicon.jpg'); setLoaded(true) }}
        className="w-full h-full object-cover"
        style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.3s ease' }}
      />
    </div>
  )
}

// ─── Score helpers ────────────────────────────────────────────
// Fallback-only estimates, used solely when an entry is missing the real
// score field from the API (normalizeEntry below prefers u.socialScore /
// u.stakingScore / u.consistencyScore whenever the backend provides them,
// which LeaderboardEntry already does for stakingScore — see
// leaderboard.service.ts). calcStakingScore now applies the real
// stakingFactor/weight from cis.service.ts so it's at least the right
// order of magnitude. calcSocialScore/calcConsistencyScore are still
// rough placeholders that don't match CIS_POINTS (post/likeReceived/
// commentReceived/streakLoginDay) — flagged as a follow-up, left
// untouched for this pass since the API doesn't expose those raw fields
// as fallback triggers anyway (socialScore/consistencyScore aren't on
// LeaderboardEntry, so these two always run — worth a closer look later).
function calcSocialScore(likes, posts, comments) {
  return Math.min(SOCIAL_MAX, posts * 10 + likes * 5 + comments * 8)
}

function calcStakingScore(cltStaked, lockDaysRemaining) {
  // Real backend formula (cis.service.ts calculateLockScore + recalculate):
  // for each stake, amountStaked × lockMultiplier(totalLockDays) ×
  // timeBonus(0.5 at lock start, ramping linearly to 1.0 at maturity),
  // summed across stakes, then × CIS_POINTS.stakingFactor (0.2) × the
  // admin-configured staking weight (defaults to 1). Leaderboard entries
  // only expose lockDaysRemaining (not total lock days or elapsed time),
  // so lockMultiplier is approximated from the same tier boundaries as
  // getLockMultiplier, and timeBonus is assumed at its 0.75 midpoint
  // rather than computed exactly. This is a real fallback improvement
  // over the previous flat ×4 (which had no basis in the backend formula
  // and overstated scores roughly 10x+) but is still an approximation.
  if (!cltStaked) return 0
  const approxLockMultiplier =
    lockDaysRemaining >= 540 ? 2.0 :
    lockDaysRemaining >= 360 ? 1.6 :
    lockDaysRemaining >= 180 ? 1.25 :
    lockDaysRemaining >= 90  ? 1.0  :
    0.5 + (lockDaysRemaining / 90) * 0.5
  const approxTimeBonus = 0.75
  const stakingFactor = 0.2
  return Math.min(STAKING_MAX, cltStaked * approxLockMultiplier * approxTimeBonus * stakingFactor)
}

function calcConsistencyScore(streakDays) {
  return Math.min(CONSISTENCY_MAX, streakDays * 3)
}

// ─── Normalize entry ──────────────────────────────────────────
// Backend returns: rank, profileId, username, fullName, avatarUrl,
//                  cisScore, likesReceived, commentsReceived
// Fields not yet in API (staking/streak/tenure) default to 0
function normalizeEntry(u, rank) {
  // Support both top-level fields (API) and nested profile object (legacy)
  const prof     = u.profile ?? {}

  // Social stats — backend uses likesReceived / commentsReceived / postCount
  const likes    = u.likesReceived  ?? u.likesCount    ?? u.likes    ?? 0
  const comments = u.commentsReceived ?? u.commentsCount ?? u.comments ?? 0
  const posts    = u.postCount ?? u.postsCount ?? u.posts ?? 0
  const engRate  = posts > 0 ? (likes + comments) / posts : 0

  // Staking — not yet in API response (backend needs to add stakedAmount, lockDays)
  const cltStaked         = u.stakedAmount ?? u.cltStaked ?? 0
  const lockDaysRemaining = u.lockDays     ?? 0

  // Consistency — backend sends streakDays at top level; fall back to nested userStat shape
  const streakDays = u.streakDays ?? u.userStat?.streak?.currentStreak ?? u.streak ?? 0
  const tenureDays = u.tenureDays ?? u.tenure  ?? 0

  const socialScore      = u.socialScore      ?? calcSocialScore(likes, posts, comments)
  const stakingScore     = u.stakingScore     ?? calcStakingScore(cltStaked, lockDaysRemaining)
  const consistencyScore = u.consistencyScore ?? calcConsistencyScore(streakDays)

  // Backend returns cisScore as the total score
  const totalScore = u.cisScore ?? u.score ?? u.totalScore ?? (socialScore + stakingScore + consistencyScore)

  return {
    id:     u.profileId ?? u.id ?? u.userId ?? String(rank),
    rank,
    name:   u.fullName  ?? prof.fullName ?? u.name   ?? 'Anonymous',
    handle: u.username  ? `@${u.username}` : (prof.username ? `@${prof.username}` : ''),
    avatar: u.avatarUrl ?? prof.avatarUrl ?? u.avatar ?? '/favicon.jpg',
    tier:   getTier(totalScore),
    totalScore, socialScore, stakingScore, consistencyScore,
    posts, likes, comments, engRate: engRate.toFixed(2),
    cltStaked, lockDaysRemaining, streakDays, tenureDays,
    rankChange: u.rankChange ?? 0,
  }
}

// ─── CIS Breakdown bars ───────────────────────────────────────
function CISBreakdown({ entry, compact = false }) {
  const bars = [
    { label: 'Social',      score: entry.socialScore,      max: SOCIAL_MAX,      color: 'bg-primary-container' },
    { label: 'Staking',     score: entry.stakingScore,     max: STAKING_MAX,     color: 'bg-blue-400' },
    { label: 'Consistency', score: entry.consistencyScore, max: CONSISTENCY_MAX, color: 'bg-secondary-container' },
  ]
  if (compact) {
    return (
      <div className="flex gap-0.5 h-1.5 w-full overflow-hidden">
        {bars.map(b => (
          <div key={b.label} title={`${b.label}: ${b.score.toLocaleString()}`}
            className={`${b.color} transition-all`} style={{ width: `${(b.score / CIS_MAX) * 100}%` }} />
        ))}
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-1.5 w-full">
      {bars.map(b => (
        <div key={b.label}>
          <div className="flex justify-between text-[9px] font-bold uppercase tracking-widest text-on-surface-variant mb-0.5">
            <span>{b.label}</span>
            <span>{b.score.toLocaleString()} / {b.max.toLocaleString()}</span>
          </div>
          <div className="h-1.5 bg-surface-container border border-on-background/10 overflow-hidden">
            <div className={`h-full ${b.color} transition-all duration-500`} style={{ width: `${(b.score / b.max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Tier badge — inline row use ─────────────────────────────
function TierBadge({ tier, compact = false }) {
  return (
    <span
      title={`${tier.label} · ${formatMultiplier(tier.multiplier)} CPT rewards`}
      className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-widest ${tier.bg} ${tier.border} ${tier.color}`}
    >
      <TierIcon src={tier.icon} alt={tier.label} size={16} />
      {!compact && tier.label}
    </span>
  )
}

// ─── UserCell ─────────────────────────────────────────────────
// FIX: clickable — navigates to the user's profile (own profile if it's the current user)
function UserCell({ entry, showBreakdown = false, onClick }) {
  const clickable = typeof onClick === 'function'
  return (
    <div
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } } : undefined}
      className={`flex items-center gap-2 lg:gap-3 min-w-0 ${clickable ? 'cursor-pointer group' : ''}`}
    >
      <div className="relative shrink-0">
        <Avatar src={entry.avatar} alt={entry.name} size="w-8 h-8 lg:w-10 lg:h-10" />
        <span className="absolute -top-2 -right-2">
          <TierIcon src={entry.tier.icon} alt={entry.tier.label} size={20} />
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <p className={`font-bold text-on-background text-xs lg:text-sm truncate ${clickable ? 'group-hover:underline' : ''}`}>{entry.name}</p>
        <p className="text-[9px] lg:text-[10px] text-on-surface-variant font-mono">{entry.handle}</p>
        {showBreakdown && <div className="mt-1 w-24"><CISBreakdown entry={entry} compact /></div>}
      </div>
    </div>
  )
}

// ─── Shared primitives ────────────────────────────────────────
function RankCell({ entry }) {
  return <span className={`font-black text-base lg:text-lg ${entry.tier.color}`}>{String(entry.rank).padStart(2, '0')}</span>
}

function TR({ entry, children }) {
  return <tr className={`${entry.tier.bg} border-b border-on-background/10 hover:brightness-95 transition-all`}>{children}</tr>
}

// FIX: table header — use dark charcoal instead of pure black bg-on-background
const TH = ({ children, right, hidden }) => (
  <th className={`p-2 lg:p-4 text-[9px] lg:text-[11px] uppercase tracking-widest font-extrabold bg-[#1c1c1e] text-white/90 ${right ? 'text-right' : 'text-left'} ${hidden ? 'hidden sm:table-cell' : ''}`}>
    {children}
  </th>
)
const TD = ({ children, right, hidden, bold }) => (
  <td className={`p-2 lg:p-4 text-xs lg:text-sm ${right ? 'text-right' : ''} ${hidden ? 'hidden sm:table-cell' : ''} ${bold ? 'font-black text-primary-container' : 'font-mono'}`}>
    {children}
  </td>
)

// ─── Tables ───────────────────────────────────────────────────
function InfluenceTable({ entries, onUserClick }) {
  return (
    <table className="w-full">
      <thead><tr>
        <TH>#</TH><TH>User</TH><TH right>CIS Score</TH><TH right hidden>Tier</TH>
      </tr></thead>
      <tbody>
        {entries.map(e => (
          <TR key={e.id} entry={e}>
            <TD><RankCell entry={e} /></TD>
            <td className="p-2 lg:p-4"><UserCell entry={e} onClick={() => onUserClick?.(e)} /></td>
            <TD right bold>{e.totalScore.toLocaleString()}</TD>
            <TD right hidden><TierBadge tier={e.tier} /></TD>
          </TR>
        ))}
      </tbody>
    </table>
  )
}

// FIX: CreatorsTable — Eng. Rate column removed; posts now reflects real postCount from API
function CreatorsTable({ entries, onUserClick }) {
  return (
    <table className="w-full">
      <thead><tr>
        <TH>#</TH><TH>Creator</TH><TH right>Posts</TH><TH right>Likes</TH><TH right>Comments</TH>
      </tr></thead>
      <tbody>
        {entries.map(e => (
          <TR key={e.id} entry={e}>
            <TD><RankCell entry={e} /></TD>
            <td className="p-2 lg:p-4"><UserCell entry={e} onClick={() => onUserClick?.(e)} /></td>
            <TD right bold>{e.posts.toLocaleString()}</TD>
            <TD right bold>{e.likes.toLocaleString()}</TD>
            <TD right bold>{e.comments.toLocaleString()}</TD>
          </TR>
        ))}
      </tbody>
    </table>
  )
}

// FIX: StakersTable — ranked independently by stakingScore
function StakersTable({ entries, onUserClick }) {
  return (
    <table className="w-full">
      <thead><tr>
        <TH>#</TH><TH>Staker</TH><TH right>CLT Staked</TH><TH right hidden>Lock Left</TH><TH right>Staking Score</TH>
      </tr></thead>
      <tbody>
        {entries.map(e => (
          <TR key={e.id} entry={e}>
            <TD><RankCell entry={e} /></TD>
            <td className="p-2 lg:p-4"><UserCell entry={e} onClick={() => onUserClick?.(e)} /></td>
            <TD right bold>{Number(e.cltStaked).toLocaleString()}<span className="text-[9px] font-normal text-on-surface-variant"> CLT</span></TD>
            <td className="p-2 lg:p-4 text-right hidden sm:table-cell">
              <span className="px-2 py-0.5 border border-on-background/20 bg-surface-container text-[10px] font-bold">{e.lockDaysRemaining}D</span>
            </td>
            <TD right bold>{e.stakingScore.toLocaleString()}</TD>
          </TR>
        ))}
      </tbody>
    </table>
  )
}

// FIX: ConsistencyTable — ranked independently by consistencyScore, shows streak + consistency columns (Tenure removed)
function ConsistencyTable({ entries, onUserClick }) {
  return (
    <table className="w-full">
      <thead><tr>
        <TH>#</TH><TH>User</TH><TH right>Streak</TH><TH right>Consistency</TH>
      </tr></thead>
      <tbody>
        {entries.map(e => (
          <TR key={e.id} entry={e}>
            <TD><RankCell entry={e} /></TD>
            <td className="p-2 lg:p-4"><UserCell entry={e} onClick={() => onUserClick?.(e)} /></td>
            <td className="p-2 lg:p-4 text-right">
              <span className="font-black text-xs text-primary-container inline-flex items-center gap-1">
                {e.streakDays}
                <TierIcon src="/icons/consistency.png" alt="streak" size={14} />
              </span>
            </td>
            <TD right bold>{e.consistencyScore.toLocaleString()}</TD>
          </TR>
        ))}
      </tbody>
    </table>
  )
}

// ─── Podium ───────────────────────────────────────────────────
// Square avatar with a medal-colored frame — the focal point of each card.
function PodiumAvatar({ src, alt, ringClass, size }) {
  const [loaded, setLoaded] = useState(false)
  const [imgSrc, setImgSrc] = useState(src)

  // BUG FIX: useState(src) only seeds imgSrc on first mount. When the
  // podium re-sorts (e.g. switching CIS → Creators tab), the same
  // PodiumAvatar instance gets a new `src` prop for a different person,
  // but imgSrc never updated — so the old photo stayed frozen in place
  // while the name/score next to it updated immediately. Re-sync whenever
  // the incoming src actually changes, and reset the loading shimmer too
  // so the new photo doesn't inherit the previous person's loaded state.
  useEffect(() => {
    setImgSrc(src)
    setLoaded(false)
  }, [src])

  return (
    <div
      className={`relative shrink-0 overflow-hidden bg-surface-container border-[3px] lg:border-4 ${ringClass}`}
      style={{ width: size, height: size }}
    >
      {!loaded && <div className="absolute inset-0 bg-on-background/10 animate-pulse" />}
      <img
        src={imgSrc}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onError={() => { setImgSrc('/favicon.jpg'); setLoaded(true) }}
        className="w-full h-full object-cover"
        style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.3s ease' }}
      />
    </div>
  )
}

// avatarPxMobile keeps 1st clearly larger than 2nd/3rd (hero hierarchy),
// while never dropping below the 56px floor on any rank.
const PODIUM_CFG = {
  1: {
    label: '1st',
    medal: '/icons/gold.png',
    ring: 'border-primary-container',
    cardBg: 'bg-primary-container/15',
    cardBorder: 'border-primary-container',
    labelColor: 'text-primary-container',
    avatarPx: 88,
    avatarPxMobile: 72,
    order: 'order-1 sm:order-2',
    lift: 'mt-0 sm:-mt-4 lg:-mt-5',
    scale: 'scale-100 sm:scale-[1.04]',
    z: 'z-10',
  },
  2: {
    label: '2nd',
    medal: '/icons/silver.png',
    ring: 'border-on-surface-variant',
    cardBg: 'bg-secondary-container/30',
    cardBorder: 'border-on-background/15',
    labelColor: 'text-on-surface-variant',
    avatarPx: 68,
    avatarPxMobile: 58,
    order: 'order-2 sm:order-1',
    lift: 'mt-0 sm:mt-3 lg:mt-4',
    scale: '',
    z: 'z-0',
  },
  3: {
    label: '3rd',
    medal: '/icons/bronze.png',
    ring: 'border-amber-700',
    cardBg: 'bg-surface-container-high',
    cardBorder: 'border-on-background/15',
    labelColor: 'text-amber-700',
    avatarPx: 68,
    avatarPxMobile: 58,
    order: 'order-3 sm:order-3',
    lift: 'mt-0 sm:mt-4 lg:mt-5',
    scale: '',
    z: 'z-0',
  },
}

function PodiumCard({ entry, rank, onClick }) {
  const cfg = PODIUM_CFG[rank]
  const clickable = typeof onClick === 'function'
  return (
    <div className={`flex-1 flex flex-col ${cfg.order} ${cfg.lift} ${cfg.scale} ${cfg.z}`}>
      <div
        onClick={onClick}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } } : undefined}
        className={`flex flex-col items-center text-center border-2 lg:neo-border px-2 pt-2.5 pb-2 sm:pt-3 sm:pb-2.5 lg:px-3 lg:pt-4 lg:pb-3 gap-1 sm:gap-1.5 ${cfg.cardBg} ${cfg.cardBorder} ${clickable ? 'cursor-pointer hover:brightness-95 transition-all group' : ''}`}
        style={rank === 1 ? { boxShadow: '5px 5px 0px 0px var(--neo-shadow-color)' } : undefined}
      >
        {/* Top: avatar — the focal element. Mobile size differs per rank
            (1st stays visibly larger) but never drops below 56px. */}
        <div className="sm:hidden">
          <PodiumAvatar src={entry.avatar} alt={entry.name} ringClass={cfg.ring} size={cfg.avatarPxMobile} />
        </div>
        <div className="hidden sm:block">
          <PodiumAvatar src={entry.avatar} alt={entry.name} ringClass={cfg.ring} size={cfg.avatarPx} />
        </div>

        {/* Middle: username + rank */}
        <div className="min-w-0 w-full">
          <p className={`font-extrabold text-on-background text-[12px] sm:text-[11px] lg:text-xs truncate leading-tight ${clickable ? 'group-hover:underline' : ''}`}>{entry.name}</p>
          <p className={`text-[9px] lg:text-[10px] font-bold uppercase tracking-widest ${cfg.labelColor}`}>{cfg.label} Place</p>
        </div>

        {/* Bottom: medal + score */}
        <div className="flex flex-col items-center gap-0.5 pt-1 border-t border-on-background/10 w-full">
          <IllustrationIcon src={cfg.medal} alt={`${cfg.label} place medal`} size={rank === 1 ? 28 : 22} />
          <span className="text-xs lg:text-sm font-extrabold text-on-background leading-none">
            {entry.totalScore.toLocaleString()}
          </span>
          <span className="text-[7px] uppercase tracking-widest font-bold text-on-surface-variant/70">CIS Score</span>
        </div>
      </div>
    </div>
  )
}

function Podium({ entries, onUserClick }) {
  if (entries.length < 3) return null
  const [first, second, third] = entries
  return (
    <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-2.5 lg:gap-3 px-1">
      {/* 1st place: full width hero on top, mobile only */}
      <div className="sm:hidden">
        <PodiumCard key={first.id} entry={first} rank={1} onClick={() => onUserClick?.(first)} />
      </div>
      {/* 2nd + 3rd: side-by-side underneath, mobile only */}
      <div className="flex sm:hidden gap-2">
        <PodiumCard key={second.id} entry={second} rank={2} onClick={() => onUserClick?.(second)} />
        <PodiumCard key={third.id} entry={third} rank={3} onClick={() => onUserClick?.(third)} />
      </div>

      {/* Desktop/tablet: original side-by-side hero layout, unchanged */}
      <div className="hidden sm:contents">
        <PodiumCard key={second.id} entry={second} rank={2} onClick={() => onUserClick?.(second)} />
        <PodiumCard key={first.id} entry={first} rank={1} onClick={() => onUserClick?.(first)} />
        <PodiumCard key={third.id} entry={third} rank={3} onClick={() => onUserClick?.(third)} />
      </div>
    </div>
  )
}

// ─── Empty State ──────────────────────────────────────────────
const EMPTY_STATE_CFG = {
  influence:   { icon: '/icons/creators.png',    title: 'No scores yet',          desc: 'Influence scores will appear once users start posting, staking and building streaks.' },
  creators:    { icon: '/icons/creators.png',    title: 'No creator activity',    desc: 'Post, get likes and comments to climb the creators board.' },
  stakers:     { icon: '/icons/stakers.png',     title: 'No stakers yet',         desc: 'Stake CLT tokens to appear here and earn Staking Power.' },
  consistency: { icon: '/icons/consistency.png', title: 'No streaks recorded',    desc: 'Stay active daily to build a streak and consistency score.' },
}

function EmptyState({ tabKey }) {
  const { icon, title, desc } = EMPTY_STATE_CFG[tabKey] ?? EMPTY_STATE_CFG.influence
  return (
    <div className="flex flex-col items-center py-16 px-6 gap-4 text-center">
      <IllustrationIcon src={icon} alt={title} size={112} className="opacity-40" />
      <p className="text-base font-extrabold uppercase tracking-wide text-on-background">{title}</p>
      <p className="text-sm text-on-surface-variant max-w-xs leading-relaxed">{desc}</p>
    </div>
  )
}

// ─── Table skeleton ───────────────────────────────────────────
function TableSkeleton() {
  return (
    <div className="flex flex-col gap-0">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 lg:p-4 border-b border-on-background/5 animate-pulse">
          <div className="w-6 h-4 bg-on-background/10 shrink-0" />
          <div className="w-9 h-9 bg-on-background/10 shrink-0" />
          <div className="flex-1 flex flex-col gap-2">
            <div className="h-3 bg-on-background/10 w-28 lg:w-40" />
            <div className="h-2 bg-on-background/10 w-16 lg:w-24" />
          </div>
          <div className="w-14 h-4 bg-on-background/10" />
        </div>
      ))}
    </div>
  )
}

// ─── Tier legend ──────────────────────────────────────────────
function TierLegend() {
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {TIERS.map(t => (
          <div
            key={t.key}
            className={`relative border-2 ${t.border} overflow-hidden`}
            style={{ minHeight: 120 }}
          >
            <img
              src={t.icon}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-contain scale-125"
              style={{ filter: 'blur(1px)', opacity: 0.5 }}
            />
            <div className="relative z-10 p-4 flex flex-col justify-end h-full gap-1" style={{ minHeight: 120 }}>
              <p className={`font-extrabold text-[13px] uppercase tracking-widest ${t.color}`}>{t.label}</p>
              <p className="text-[10px] text-on-surface-variant font-mono">
                {t.min.toLocaleString()} {t.max == null ? '+' : `– ${t.max.toLocaleString()}`}
              </p>
              <p className="text-[10px] font-bold text-on-background">{formatMultiplier(t.multiplier)} CPT rewards</p>
            </div>
          </div>
        ))}
      </div>
      <Link
        to="/levels"
        className="self-start text-[10px] font-bold uppercase tracking-widest text-primary-container hover:underline"
      >
        How CIS → CPT works →
      </Link>
    </div>
  )
}

// ─── My Stats banner ─────────────────────────────────────────
// FIX: show tab-relevant stats per active tab, not always CIS
function MyStatsBanner({ currentUser, activeTab }) {
  if (!currentUser) return null
  const e   = normalizeEntry(currentUser, currentUser.rank ?? '—')
  const t   = e.tier
  const pct = Math.round((e.totalScore / CIS_MAX) * 100)

  // Tab-specific stat pills
  const statPills = {
    influence: [
      { label: 'Posts',  value: e.posts },
      { label: 'Likes',  value: e.likes.toLocaleString() },
      { label: 'Streak', value: e.streakDays },
      { label: 'CLT',    value: Number(e.cltStaked).toLocaleString() },
    ],
    creators: [
      { label: 'Posts',    value: e.posts },
      { label: 'Likes',    value: e.likes.toLocaleString() },
      { label: 'Comments', value: e.comments.toLocaleString() },
    ],
    stakers: [
      { label: 'CLT Staked', value: Number(e.cltStaked).toLocaleString() },
      { label: 'Lock Left',  value: `${e.lockDaysRemaining}D` },
      { label: 'Stk Score',  value: e.stakingScore.toLocaleString() },
    ],
    consistency: [
      { label: 'Streak',     value: e.streakDays },
      { label: 'Consistency',value: e.consistencyScore.toLocaleString() },
    ],
  }

  const pills = statPills[activeTab] ?? statPills.influence

  return (
    <section className="bg-on-background text-surface border-4 border-on-background p-4 lg:p-6"
      style={{ boxShadow: '4px 4px 0px 0px var(--neo-shadow-color)' }}>
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase font-bold text-surface/50 mb-0.5">Your Rank</p>
          <p className="text-[40px] lg:text-[52px] font-extrabold leading-none">#{e.rank}</p>
          <TierBadge tier={t} />
        </div>
        <div className="flex-1 max-w-xs">
          <div className="flex justify-between items-baseline mb-1">
            <p className="text-[10px] uppercase font-bold text-surface/50">CIS Score</p>
            <p className="font-extrabold text-primary-container text-2xl">{e.totalScore.toLocaleString()}</p>
          </div>
          <div className="h-2 bg-surface/10 border border-surface/20 overflow-hidden mb-2">
            <div className="h-full bg-primary-container transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
          <CISBreakdown entry={e} />
        </div>
        <div className="flex gap-2 flex-wrap">
          {pills.map((st, i) => (
            <div key={i} className="flex flex-col items-center px-3 py-2 border border-surface/20 bg-surface/5 min-w-[56px]">
              <span className="font-black text-xs lg:text-sm">{st.value}</span>
              <span className="text-[8px] lg:text-[9px] uppercase tracking-widest text-surface/50 font-bold">{st.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Main ─────────────────────────────────────────────────────
export default function Leaderboard() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [activeTab,    setActiveTab]    = useState('influence')
  const [entries,      setEntries]      = useState([])
  // Separate from `entries` (which is the CIS top-20) — the Stakers tab
  // used to just re-sort that same CIS list, so non-stakers showed up at
  // 0 CLT and real stakers outside the CIS top-20 never appeared at all.
  // This comes from its own backend ranking that only includes profiles
  // with an active stake.
  const [stakersEntries, setStakersEntries] = useState([])
  const [stakersLoading, setStakersLoading] = useState(false)
  const [currentUser,  setCurrentUser]  = useState(null)
  const [loading,      setLoading]      = useState(true)
  const [lastUpdated,  setLastUpdated]  = useState(null)
  const [live,         setLive]         = useState(false)
  const [refreshing,   setRefreshing]   = useState(false)
  const socketRef = useRef(null)
  const currentTab = TABS.find(t => t.key === activeTab)

  // FIX: navigate to a leaderboard entry's profile — own profile page if it's
  // the current user, same convention used by PostCard/CommentsPage.
  const handleUserClick = useCallback((entry) => {
    if (!entry?.id) return
    if (user?.id && entry.id === user.id) navigate('/profile')
    else navigate(`/profile/${entry.id}`)
  }, [navigate, user?.id])

  const applySnapshot = useCallback(({ top = [], currentUser: cu = null }) => {
    setEntries(top.map((u, i) => normalizeEntry(u, i + 1)))
    setCurrentUser(cu)
    setLastUpdated(new Date())
  }, [])

  // FIX: /api/leaderboard doesn't return userStat.streak, so streakDays is
  // always 0 from that endpoint alone. /api/profiles/{id} does include it
  // (see UserProfile.jsx), so fetch each entry's real profile and patch the
  // streak in — same data, same read path, just applied across the board
  // instead of one profile at a time.
  useEffect(() => {
    if (entries.length === 0) return

    let cancelled = false

    Promise.all(
      entries.map((e) =>
        getProfile(e.id)
          .then((p) => ({ id: e.id, streak: p?.streak ?? 0 }))
          .catch(() => ({ id: e.id, streak: e.streakDays })),
      ),
    ).then((results) => {
      if (cancelled) return
      const streakById = new Map(results.map((r) => [r.id, r.streak]))
      setEntries((prev) =>
        prev.map((e) => {
          if (!streakById.has(e.id)) return e
          const streakDays = streakById.get(e.id)
          // Recompute consistencyScore from the freshly patched streak so the
          // Consistency table/sort isn't left using the stale, pre-patch
          // value derived from streak=0. totalScore is intentionally left
          // untouched — it's the real backend cisScore (includes referral
          // bonus etc.), not a local sum, and must not be overwritten here.
          const consistencyScore = calcConsistencyScore(streakDays)
          return { ...e, streakDays, consistencyScore }
        }),
      )
    })

    return () => { cancelled = true }
    // Only re-run when the entry set itself changes (new ids), not on every
    // streak patch — otherwise this effect would re-trigger itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries.map((e) => e.id).join(',')])

  // FIX: same streak enrichment for the "Your Rank" banner's currentUser,
  // which also comes from /api/leaderboard and lacks userStat.streak.
  useEffect(() => {
    if (!currentUser) return

    let cancelled = false
    const id = currentUser.profileId ?? currentUser.id

    getProfile(id)
      .then((p) => {
        if (cancelled) return
        setCurrentUser((prev) => (prev ? { ...prev, streakDays: p?.streak ?? prev.streakDays } : prev))
      })
      .catch(() => {})

    return () => { cancelled = true }
    // Only re-run when the user identity changes, not on every streak patch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.profileId ?? currentUser?.id])

  useEffect(() => {
    if (activeTab !== 'stakers') return
    let cancelled = false
    setStakersLoading(true)
    getTopStakers()
      .then((data) => {
        if (cancelled) return
        const list = Array.isArray(data) ? data : []
        setStakersEntries(list.map((u, i) => normalizeEntry(u, i + 1)))
      })
      .catch(() => { if (!cancelled) setStakersEntries([]) })
      .finally(() => { if (!cancelled) setStakersLoading(false) })
    return () => { cancelled = true }
  }, [activeTab])

  useEffect(() => {
    setLoading(true)
    getLeaderboard()
      .then(({ top20 = [], currentUser: cu = null }) => applySnapshot({ top: top20, currentUser: cu }))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false))
  }, [applySnapshot])

  useEffect(() => {
    const token = getAccessToken()
    if (!token || !SOCKET_ORIGIN) return

    const socket = io(`${SOCKET_ORIGIN}/leaderboard`, {
      auth: { token },
      transports: ['websocket'],
    })
    socketRef.current = socket

    socket.on('connect', () => setLive(true))
    socket.on('disconnect', () => setLive(false))
    socket.on('connect_error', () => setLive(false))

    socket.on('leaderboard:update', (payload) => {
      applySnapshot(payload)
      setLoading(false)
      setRefreshing(false)
    })

    socket.on('leaderboard:error', () => setRefreshing(false))

    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [applySnapshot])

  const handleRefresh = () => {
    if (socketRef.current?.connected) {
      setRefreshing(true)
      socketRef.current.emit('leaderboard:refresh')
    } else {
      setRefreshing(true)
      getLeaderboard()
        .then(({ top20 = [], currentUser: cu = null }) => applySnapshot({ top: top20, currentUser: cu }))
        .catch(() => {})
        .finally(() => setRefreshing(false))
    }
  }

  // FIX: each tab sorts and re-ranks independently by its own metric, capped to top 10.
  // Stakers is the exception — it's already its own backend-ranked, stakers-only
  // list (see getTopStakers effect above), so it's used as-is, not re-derived
  // from the CIS top-20 like the other tabs.
  const sorted = activeTab === 'stakers'
    ? stakersEntries
    : [...entries].sort((a, b) => {
        if (activeTab === 'consistency') return b.consistencyScore - a.consistencyScore
        if (activeTab === 'creators')    return b.socialScore      - a.socialScore
        return b.totalScore - a.totalScore
      }).slice(0, 10).map((e, i) => ({ ...e, rank: i + 1 }))

  const isLoading = activeTab === 'stakers' ? stakersLoading : loading

  return (
    <div className="flex-1 lg:ml-[300px] w-full max-w-4xl flex flex-col gap-3 lg:gap-5">

      {/* Header */}
      <section className="border border-on-background/10 lg:neo-border lg:neo-shadow p-4 lg:p-8 bg-surface-container">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary-container mb-1">Chicago Influence Score</p>
        <h1 className="text-[1.6rem] lg:text-display-lg font-extrabold uppercase tracking-tight leading-none mb-2">Leaderboard</h1>
        <p className="text-sm text-on-surface-variant max-w-xl">{currentTab.desc}</p>
        <div className="flex items-center gap-2 mt-1.5">
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${live ? 'text-green-600' : 'text-on-surface-variant/50'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-green-500 animate-pulse' : 'bg-on-surface-variant/40'}`} />
            {live ? 'Live' : 'Offline'}
          </span>
          {lastUpdated && (
            <p className="text-[10px] text-on-surface-variant/50">· Last updated: {lastUpdated.toLocaleTimeString()}</p>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="ml-auto text-[10px] font-bold uppercase tracking-wider text-primary-container hover:underline disabled:opacity-50"
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </section>

      {/* Tier legend */}
      <TierLegend />

      {/* Tab bar */}
      <div className="flex border border-on-background/10 lg:neo-border overflow-x-auto">
        {TABS.map(tab => {
          const active = activeTab === tab.key
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`relative flex-1 min-w-[64px] overflow-hidden border-r border-on-background/10 last:border-r-0 transition-all
                ${active
                  ? 'bg-primary-container text-on-primary-fixed'
                  : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
                }`}>
              <img
                src={tab.icon}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-contain scale-150 pointer-events-none"
                style={{ filter: 'blur(3px)', opacity: active ? 0.35 : 0.2 }}
              />
              <span className="relative z-10 flex items-center justify-center py-2.5 lg:py-3.5 px-1 lg:px-3 whitespace-nowrap">
                <span className="text-[10px] lg:text-[12px] font-bold uppercase tracking-wider">{tab.label}</span>
              </span>
            </button>
          )
        })}
      </div>

      {/* Podium */}
      {!isLoading && sorted.length >= 3 && (
        <div className="pt-1">
          <Podium entries={sorted} onUserClick={handleUserClick} />
        </div>
      )}

      {/* Table */}
      <section className="border border-on-background/10 lg:neo-border lg:neo-shadow overflow-hidden">
        {isLoading ? (
          <TableSkeleton />
        ) : sorted.length === 0 ? (
          <EmptyState tabKey={activeTab} />
        ) : (
          <>
            {activeTab === 'influence'   && <InfluenceTable   entries={sorted} onUserClick={handleUserClick} />}
            {activeTab === 'creators'    && <CreatorsTable    entries={sorted} onUserClick={handleUserClick} />}
            {activeTab === 'stakers'     && <StakersTable     entries={sorted} onUserClick={handleUserClick} />}
            {activeTab === 'consistency' && <ConsistencyTable entries={sorted} onUserClick={handleUserClick} />}
          </>
        )}
      </section>

      {/* FIX: pass activeTab so banner shows tab-relevant stats */}
      <MyStatsBanner currentUser={currentUser} activeTab={activeTab} onUserClick={handleUserClick} />
      <div className="h-20 md:h-0" />
    </div>
  )
}
